import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr, localizedStatic } from "@/i18n/copy";
import WorkflowGuide, { useWorkflowGuide } from "@/components/WorkflowGuide";
import CustomerTag from "@/components/CustomerTag";
import PracticeCustomer from "@/components/PracticeCustomer";
import Quotations from "@/pages/Quotations";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, errMsg } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import AuthenticatedApiImage from "@/components/AuthenticatedApiImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, Eye, ImagePlus, MailPlus, Plus, Search, Trash2, UserPlus, UsersRound } from "lucide-react";

const STAGE_KEYS = ["lead", "consult", "booked", "in_progress", "completed", "rejected", "cancelled"];
const PROJECT_TYPES = localizedStatic([
  ["tattoo", "Tattoo"], ["cover_up", "Cover-up"], ["restyling", "Restyling"], ["continuation", "Continuazione"], ["other", "Altro"],
], [[0,1],[1,1],[2,1],[3,1],[4,1]]);
const ORIGINS = ["PWA Cliente", "Walk-in", "Instagram", "WhatsApp", "Telefono", "Passaparola", "Google", "Altro"];
const blank = {
  title: "", customer_id: "", artist_id: "", candidate_artist_ids: [], appointment_id: "",
  description: "", placement: "", size: "", style: "", contact_origin: "", project_type: "tattoo",
  budget_min: "", budget_max: "", internal_notes: "", source_available_project_id: "", quotation_id: "",
  status: "lead", price_estimate: 0, images: []
};
const blankCustomer = {
  name: "", surname: "", email: "", phone: "", whatsapp: "", birthdate: "", fiscal_code: "",
  address: "", city: "", province: "", region: "", cap: "", country: "Italia", instagram: "", source: "",
  preferences: "", preferred_style: "", preferred_artist_id: "", ownership_type: "studio", referring_artist_id: "", notes: "", tags: []
};

export default function Projects() {
  useUiLanguage();
  const workflowGuide = useWorkflowGuide("project-create");
  const { t } = useTranslation();
  const { studio, user } = useAuth();
  const canCreateStudioPractice = ["studio_owner", "manager"].includes(user?.role);
  const projectDraftKey = `tps:draft:${studio?.id||"studio"}:${user?.id||user?.email||"operator"}:project-intake`;
  const location = useLocation();
  const navigate = useNavigate();
  const fmt = useFormat();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [artistCatalog, setArtistCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [customerMode, setCustomerMode] = useState("existing");
  const [customerQuery, setCustomerQuery] = useState("");
  const [newCustomer, setNewCustomer] = useState(blankCustomer);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [quoteOnOpen, setQuoteOnOpen] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [detailProposals, setDetailProposals] = useState([]);
  const [detailReferences, setDetailReferences] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (canCreateStudioPractice && new URLSearchParams(location.search).get("quick") === "new") {
      resetIntake();
      setOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, canCreateStudioPractice]);

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(projectDraftKey) || "null");
      if (saved?.dirty) {
        setForm({ ...blank, ...(saved.form || {}) });
        setCustomerMode(saved.customerMode || "existing");
        setCustomerQuery(saved.customerQuery || "");
        setNewCustomer({ ...blankCustomer, ...(saved.newCustomer || {}) });
        setCreatedCustomer(saved.createdCustomer || null);
        setOpen(true);
        setDraftRestored(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    const dirty = Boolean(
      form.title || form.customer_id || form.description || form.internal_notes ||
      customerQuery || newCustomer.name || newCustomer.email || newCustomer.phone
    );
    try {
      sessionStorage.setItem(projectDraftKey, JSON.stringify({
        dirty, form, customerMode, customerQuery, newCustomer, createdCustomer,
        updated_at: new Date().toISOString(),
      }));
    } catch {}
  }, [open, form, customerMode, customerQuery, newCustomer, createdCustomer]);

  const load = () => api.get("/projects").then(setList).catch(() => {});
  const loadCustomers = () => api.get("/customers").then(setCustomers).catch(() => {});
  useEffect(() => {
    load(); loadCustomers();
    api.get("/artists").then(setArtists).catch(() => {});
    api.get("/studio/available-projects").then(setArtistCatalog).catch(() => setArtistCatalog([]));
  }, []);
  useEffect(() => {
    const refresh = () => api.get("/studio/available-projects").then(setArtistCatalog).catch(() => {});
    window.addEventListener("tps:data-changed", refresh);
    return () => window.removeEventListener("tps:data-changed", refresh);
  }, []);

  const activeArtists = useMemo(() => artists.filter((artist) => artist.active !== false), [artists]);
  const artistCatalogGroups = useMemo(() => {
    const grouped = {};
    artistCatalog.forEach((project) => {
      const key = project.artist?.id || project.artist_id || "unknown";
      if (!grouped[key]) grouped[key] = { artist: project.artist || { id: key, name: "Tattoo Artist" }, rows: [] };
      grouped[key].rows.push(project);
    });
    return Object.values(grouped);
  }, [artistCatalog]);
  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 25);
    return customers.filter((c) => [c.name, c.surname, c.email, c.phone].filter(Boolean).join(" ").toLowerCase().includes(q)).slice(0, 30);
  }, [customers, customerQuery]);

  const resetIntake = (clearStored = false) => {
    setForm(blank); setCustomerMode("existing"); setCustomerQuery("");
    setNewCustomer(blankCustomer); setCreatedCustomer(null); setReferenceFiles([]); setDraftRestored(false);
    if (clearStored) { try { sessionStorage.removeItem(projectDraftKey); } catch {} }
  };
  const closeIntake = (value) => { setOpen(value); if (!value) resetIntake(true); };
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onCustomer = (k) => (e) => setNewCustomer({ ...newCustomer, [k]: e.target.value });

  const createCustomer = async (invite = false) => {
    if (!newCustomer.name.trim()) return toast.error(tr("Inserisci almeno il nome del cliente."));
    if (invite && !newCustomer.email.trim()) return toast.error(tr("Per inviare l'invito PWA serve un'email."));
    if (newCustomer.ownership_type === "artist" && !newCustomer.referring_artist_id) return toast.error(tr("Seleziona l'artista che ha portato il Cliente."));
    setSavingCustomer(true);
    try {
      const payload = {
        ...newCustomer,
        source: newCustomer.source || form.contact_origin || tr("Progetto Tattoo"),
        tags: Array.isArray(newCustomer.tags) ? newCustomer.tags : [],
      };
      const customer = await api.post("/customers", payload);
      setCreatedCustomer(customer); setForm((current) => ({ ...current, customer_id: customer.id }));
      await loadCustomers();
      if (invite) {
        const invitation = await api.post("/client/invitations", { customer_id: customer.id });
        toast.success(invitation?.delivery?.sent ? tr("Cliente creato e invito PWA inviato") : tr("Cliente creato. Invito PWA generato, consegna email da verificare."));
      } else {
        toast.success(tr("Cliente creato nel CRM e collegato al progetto"));
      }
      setCustomerMode("existing");
      setCustomerQuery([customer.name, customer.surname].filter(Boolean).join(" "));
      return customer;
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingCustomer(false); }
  };

  const toggleCandidate = (artistId) => {
    const exists = form.candidate_artist_ids.includes(artistId);
    setForm({ ...form, candidate_artist_ids: exists ? form.candidate_artist_ids.filter((id) => id !== artistId) : [...form.candidate_artist_ids, artistId] });
  };

  const save = async (attachQuote = false) => {
    if (savingProject) return;
    if (!form.title.trim()) return toast.error(tr("Inserisci il titolo/idea del progetto."));
    if (!form.customer_id) return toast.error(tr("Seleziona o crea il cliente."));
    setSavingProject(true);
    try {
      const created = await api.post("/projects", {
        ...form,
        budget_min: Number(form.budget_min) || 0,
        budget_max: Number(form.budget_max) || 0,
        price_estimate: Number(form.price_estimate) || 0,
      });
      for (const file of referenceFiles) {
        const data = new FormData();
        data.append("file", file);
        await api.post(`/projects/${created.id}/references`, data);
      }
      workflowGuide.complete();
      toast.success(referenceFiles.length ? tr("Pratica creata con {{0}} reference", {"0": referenceFiles.length}) : tr("Pratica Tattoo creata e collegata al Cliente"));
      setOpen(false); resetIntake(true); load();
      setQuoteOnOpen(attachQuote); await openProjectDetails(created);
      window.dispatchEvent(new Event("tps:data-changed"));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingProject(false); }
  };

  const setStage = async (p, status) => {
    try {
      if(status === "delete") {
        if(!window.confirm(tr("Eliminare questo progetto e le attività collegate esclusivamente ad esso? Il cliente e gli altri progetti rimangono. Incassi e documenti firmati impediscono l’eliminazione.")))return;
        await api.del(`/projects/${p.id}?confirm=true`);
        setDetailProject(null);
        toast.success(tr("Progetto eliminato."));
      } else await api.put(`/projects/${p.id}`, { ...p, status });
      await load(); window.dispatchEvent(new Event("tps:data-changed")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const custName = (id) => {
    const c = customers.find((item) => item.id === id);
    return c ? [c.name, c.surname].filter(Boolean).join(" ") : "";
  };
  const artistName = (id) => activeArtists.find((a) => a.id === id)?.name || "";
  const candidacyLabel = (status) => ({
    pending: tr("In attesa"), accepted: tr("Accettata"), rejected: tr("Rifiutata"),
    proposed: tr("Proposta inviata"), withdrawn: tr("Ritirata"),
  }[status] || status || tr("In attesa"));
  const openProjectDetails = async (project) => {
    setDetailProject(project); setDetailProposals([]); setDetailReferences([]); setLoadingDetail(true);
    try {
      const [proposals, references] = await Promise.all([
        api.get(`/projects/${project.id}/proposals`),
        api.get(`/projects/${project.id}/references`).catch(()=>[]),
      ]);
      setDetailProposals(proposals); setDetailReferences(references);
    }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoadingDetail(false); }
  };
  const removeReference = async (reference) => {
    if (!detailProject || !window.confirm(tr("Rimuovere questa reference dal brief?"))) return;
    try {
      await api.del(`/projects/${detailProject.id}/references/${reference.id}`);
      setDetailReferences((rows)=>rows.filter((item)=>item.id!==reference.id));
      toast.success(tr("Reference rimossa"));
    } catch(e) { toast.error(errMsg(e)); }
  };
  const presentProposal = async (proposal) => {
    if(!detailProject)return;
    setDetailProject({...detailProject,quote_proposal:proposal});setQuoteOnOpen(true);
  };

  return (
    <div className="animate-fade-up">{!open && canCreateStudioPractice && <WorkflowGuide guide={workflowGuide} className="mb-3"/>}
      <PageHeader subtitle={tr("Pipeline operativa: dal contatto iniziale al tattoo completato.")} title={t("projects.title")} action={canCreateStudioPractice ?
        <Dialog open={open} onOpenChange={closeIntake}>
          <DialogTrigger asChild><Button data-testid="add-project-btn"><Plus className="w-4 h-4 mr-2" /> {tr("Nuovo progetto cliente")}</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{tr("Nuovo progetto cliente")}</DialogTitle>
              <p className="text-sm text-muted-foreground">{tr("Crea una sola pratica canonica collegata a CRM, artisti e ai prossimi passaggi operativi.")}</p>
            </DialogHeader>
            <WorkflowGuide guide={workflowGuide}/>
            {draftRestored && <div className="rounded-xl border border-primary/25 bg-primary/5 p-3 text-xs text-muted-foreground"><b className="text-foreground">{tr("Lavoro ripristinato.")}</b> {tr("TPS ha riaperto la pratica esattamente dove l'avevi lasciata. I dati non sono ancora salvati nel database finché non premi “Crea pratica Tattoo”.")}</div>}

            <div className="space-y-5">
              <section className="rounded-xl border p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div><p className="label-tiny text-primary">{tr("1 · Cliente")}</p><h3 className="font-semibold">{tr("Identifica il cliente senza duplicarlo")}</h3></div>
                  {form.customer_id && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold">{tr("COLLEGATO")}</span>}
                </div>
                <PracticeCustomer customers={customers} selectedId={form.customer_id} onSelect={c=>{setForm(f=>({...f,customer_id:c.id}));setCustomerQuery([c.name,c.surname].filter(Boolean).join(" "));}} onCreated={c=>setCustomers(rows=>[c,...rows])} artists={activeArtists}/>

              </section>

              <section className="rounded-xl border p-4 space-y-3">
                <div><p className="label-tiny text-primary">{tr("2 · Brief")}</p><h3 className="font-semibold">{tr("Definisci la richiesta")}</h3></div>
                <div><Label>{tr("Titolo / idea *")}</Label><Input data-testid="proj-title" value={form.title} onChange={on("title")} placeholder={tr("Es. Manica giapponese, cover-up spalla…")}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tr("Origine contatto")}</Label><select value={form.contact_origin} onChange={on("contact_origin")} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm"><option value="">{tr("Seleziona")}</option>{ORIGINS.map((x)=><option key={x} value={x}>{tr(x)}</option>)}</select></div>
                  <div><Label>{tr("Tipologia")}</Label><select value={form.project_type} onChange={on("project_type")} className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm">{PROJECT_TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-3 gap-3"><div><Label>{tr("Zona")}</Label><Input value={form.placement} onChange={on("placement")}/></div><div><Label>{tr("Stile")}</Label><Input value={form.style} onChange={on("style")}/></div><div><Label>{tr("Dimensioni")}</Label><Input value={form.size} onChange={on("size")}/></div></div>
                <div><Label>{tr("Richiesta / brief del Cliente")}</Label><Textarea value={form.description} onChange={on("description")} placeholder={tr("Idea, elementi indispensabili, preferenze, riferimenti già consegnati…")}/></div>
                <div><Label>{tr("Reference visive")}</Label><Input className="mt-1.5" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e)=>setReferenceFiles(Array.from(e.target.files||[]).slice(0,10))}/><p className="mt-1 text-[11px] text-muted-foreground">{referenceFiles.length ? tr("{{0}} immagini pronte per essere allegate", {"0": referenceFiles.length}) : tr("Facoltativo · foto, esempi o riferimenti forniti dal Cliente. Visibili solo a Studio e artisti candidati.")}</p></div>
                <div className="grid grid-cols-2 gap-3"><div><Label>{tr("Budget da")}</Label><Input type="number" min="0" value={form.budget_min} onChange={on("budget_min")}/></div><div><Label>{tr("Budget a")}</Label><Input type="number" min="0" value={form.budget_max} onChange={on("budget_max")}/></div></div>
                <div><Label>{tr("Note interne Studio/Artista")}</Label><Textarea value={form.internal_notes} onChange={on("internal_notes")} placeholder={tr("Non visibili al Cliente.")}/></div>
              </section>

              <section className="rounded-xl border p-4">
                <div className="mb-3"><p className="label-tiny text-primary">{tr("3 · Candidature")}</p><h3 className="font-semibold">{tr("Invita uno o più artisti a valutare il progetto")}</h3><p className="mt-1 text-xs text-muted-foreground">{tr("La candidatura non assegna ancora il lavoro. L'artista definitivo verrà scelto più avanti.")}</p></div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {activeArtists.map((a)=><label key={a.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${form.candidate_artist_ids.includes(a.id)?"border-primary/40 bg-primary/5":""}`}><input type="checkbox" checked={form.candidate_artist_ids.includes(a.id)} onChange={()=>toggleCandidate(a.id)}/><span className="min-w-0"><b className="block truncate text-sm">{a.name}</b><span className="text-xs text-muted-foreground">{(() => {
  const raw = Array.isArray(a.specialties) ? a.specialties : [];
  const labels = raw.map((item) => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return "";
    return item.label || item.name || item.title || item.value || item.specialty || "";
  }).filter(Boolean);
  return labels.join(" · ") || a.specialization || tr("Tattoo Artist");
})()}</span></span></label>)}
                </div>
                {activeArtists.length===0&&<p className="text-sm text-muted-foreground">{tr("Nessun Tattoo Artist attivo disponibile.")}</p>}
              </section>
            </div>

            <DialogFooter className="mt-2"><Button disabled={savingProject} variant="outline" onClick={()=>save(true)}>{tr("Crea e allega preventivo")}</Button><Button disabled={savingProject} onClick={()=>save(false)} data-testid="proj-save"><UsersRound className="mr-2 h-4 w-4"/>{tr("Crea pratica Tattoo")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      : null} />

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {STAGE_KEYS.map((s) => {
          const items = list.filter((p) => p.status === s);
          return (
            <div key={s} data-testid={`stage-${s}`}>
              <div className="flex items-center justify-between mb-3"><span className="label-tiny">{t(`projects.stages.${s}`)}</span><span className="text-xs text-muted-foreground">{items.length}</span></div>
              <div className="space-y-3 min-h-[60px]">
                {items.map((p) => (
                  <Card key={p.id} className="p-4 bg-card/60" data-testid="project-card">
                    <div className="font-medium text-sm">{p.title}</div>
                    <div className="text-xs text-muted-foreground mt-1"><CustomerTag customerId={p.customer_id}/>{custName(p.customer_id)} · {p.style || p.project_type || "—"}</div>
                    {p.contact_origin && <div className="mt-2 text-[11px] text-muted-foreground">{tr("Origine:")} {p.contact_origin}</div>}
                    {p.candidate_artist_ids?.length > 0 && <div className="mt-2 rounded-lg border bg-background/40 px-2.5 py-2 text-[11px]"><b>{tr(p.candidate_artist_ids.length === 1 ? "{{0}} artista candidato" : "{{0}} artisti candidati", {"0": p.candidate_artist_ids.length})}</b><div className="mt-1 text-muted-foreground">{p.candidate_artist_ids.map(artistName).filter(Boolean).join(" · ")}</div></div>}
                    {(p.budget_min > 0 || p.budget_max > 0) && <div className="text-xs text-primary mt-2">{tr("Budget")} {p.budget_min>0?fmt.c(p.budget_min):"—"}{p.budget_max>0?` – ${fmt.c(p.budget_max)}`:""}</div>}
                    {p.candidate_summary?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{p.candidate_summary.map((candidate)=><span key={candidate.id} className="rounded-full border px-2 py-0.5 text-[10px]">{artistName(candidate.artist_id)} · {candidacyLabel(candidate.status)}</span>)}</div>}
                    <Button type="button" variant="outline" size="sm" className="mt-3 w-full justify-start" onClick={()=>openProjectDetails(p)}><Eye className="mr-2 h-3.5 w-3.5"/>{tr("Apri pratica")}</Button>
                    <Select value={p.status} onValueChange={(v) => setStage(p, v)}>
                      <SelectTrigger className="mt-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGE_KEYS.map((x) => <SelectItem key={x} value={x}>{t(`projects.stages.${x}`)}</SelectItem>)}{canCreateStudioPractice&&<SelectItem value="delete">{tr("Elimina…")}</SelectItem>}</SelectContent>
                    </Select>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <section className="mt-8 rounded-2xl border bg-card/40 p-4 sm:p-5">
        <div><p className="label-tiny text-primary">{tr("Cataloghi artisti")}</p><h2 className="font-display text-xl font-semibold">{tr("Progetti disponibili degli artisti")}</h2><p className="mt-1 text-sm text-muted-foreground">{tr("Vista specchio in sola lettura. Ogni Tattoo Artist resta proprietario e gestore del proprio catalogo.")}</p></div>
        <div className="mt-4 space-y-3">
          {artistCatalogGroups.map(({artist,rows})=><details key={artist.id} className="group rounded-xl border bg-background/30">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4"><div><b>{artist.name||tr("Tattoo Artist")}</b><p className="mt-0.5 text-xs text-muted-foreground">{tr(rows.length === 1 ? "{{0}} progetto" : "{{0}} progetti", {"0": rows.length})} · {rows.filter((item)=>item.status==="available").length} {tr("disponibili")}</p></div><ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"/></summary>
            <div className="grid grid-cols-2 gap-3 border-t p-4 sm:grid-cols-3 lg:grid-cols-5">
              {rows.map((item)=><article key={item.id} className="overflow-hidden rounded-xl border bg-card/50">
                <AuthenticatedApiImage path={item.image_url||""} alt={item.title||tr("Progetto")} className="aspect-[3/4] w-full object-cover" fallback={<div className="grid aspect-[3/4] w-full place-items-center bg-muted/20 text-[10px] text-muted-foreground">{tr("Anteprima")}</div>}/>
                <div className="p-2.5"><div className="flex flex-wrap gap-1"><span className={"rounded-full border px-1.5 py-0.5 text-[9px] font-semibold "+(item.status==="available"?"border-emerald-500/30 text-emerald-500":"text-muted-foreground")}>{item.status==="available"?tr("DISPONIBILE"):item.status==="reserved"?tr("IN OPZIONE"):item.status==="assigned"?tr("ASSEGNATO"):item.status==="completed"?tr("REALIZZATO"):String(item.status||"").toUpperCase()}</span>{item.requires_consultation&&<span className="rounded-full border border-primary/30 px-1.5 py-0.5 text-[9px] text-primary">{tr("CONSULENZA")}</span>}</div><b className="mt-2 block truncate text-xs">{item.title||tr("Progetto")}</b><p className="mt-1 truncate text-[10px] text-muted-foreground">{[item.style,item.body_area,item.size_hint].filter(Boolean).join(" · ")}</p></div>
              </article>)}
            </div>
          </details>)}
          {!artistCatalogGroups.length&&<div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">{tr("Nessun progetto artista ancora pubblicato.")}</div>}
        </div>
      </section>

      <Dialog open={Boolean(detailProject)} onOpenChange={(value)=>{ if(!value){setQuoteOnOpen(false);setDetailProject(null);setDetailProposals([]);setDetailReferences([]);} }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {detailProject && <>
            <DialogHeader>
              <DialogTitle>{detailProject.title}</DialogTitle>
              <p className="text-sm text-muted-foreground"><CustomerTag customerId={detailProject.customer_id}/>{custName(detailProject.customer_id)} · {[detailProject.project_type, detailProject.placement, detailProject.style, detailProject.size].filter(Boolean).join(" · ")}</p>
            </DialogHeader>
            <div className="space-y-4">
              {canCreateStudioPractice && <label className="block text-sm">{tr("Artista incaricato per il preventivo")}<select className="mt-2 h-11 w-full rounded-lg border bg-background px-3" value={detailProject.artist_id||""} onChange={async(e)=>{try{const updated=await api.put(`/projects/${detailProject.id}`,{...detailProject,artist_id:e.target.value});setDetailProject(updated);load();window.dispatchEvent(new Event("tps:data-changed"));}catch(error){toast.error(errMsg(error));}}}><option value="">{tr("Da definire dopo le candidature")}</option>{activeArtists.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}</select></label>}
              {canCreateStudioPractice && <Quotations key={`${detailProject.id}:${detailProject.artist_id}:${detailProject.quote_proposal?.id||""}`} projectContext={detailProject} initiallyOpen={quoteOnOpen} />}
              <section className="rounded-xl border p-4">
                <p className="label-tiny text-primary">{tr("Brief cliente")}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm">{detailProject.description || tr("Nessuna descrizione aggiuntiva.")}</p>
                {detailProject.internal_notes && <div className="mt-3 rounded-lg border bg-background/50 p-3 text-sm"><b>{tr("Note interne")}</b><p className="mt-1 whitespace-pre-wrap text-muted-foreground">{detailProject.internal_notes}</p></div>}
              </section>

              {detailReferences.length>0&&<section className="rounded-xl border p-4">
                <div className="mb-3"><p className="label-tiny text-primary">{tr("Reference")}</p><h3 className="font-semibold">{tr("Materiale visivo del brief")}</h3></div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{detailReferences.map((reference)=><article key={reference.id} className="group/reference relative overflow-hidden rounded-xl border bg-card/50"><AuthenticatedApiImage path={reference.image_url} alt={reference.original_filename||tr("Reference")} className="aspect-square w-full object-cover" fallback={<div className="grid aspect-square place-items-center text-xs text-muted-foreground">{tr("Reference")}</div>}/><button type="button" onClick={()=>removeReference(reference)} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full border bg-background/90 opacity-0 transition-opacity group-hover/reference:opacity-100"><Trash2 className="h-3.5 w-3.5"/></button></article>)}</div>
              </section>}

              <section className="rounded-xl border p-4">
                <div className="mb-3"><p className="label-tiny text-primary">{tr("Candidature")}</p><h3 className="font-semibold">{tr("Stato degli artisti coinvolti")}</h3></div>
                <div className="space-y-2">
                  {(detailProject.candidate_summary || []).map((candidate)=><div key={candidate.id} className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"><b>{artistName(candidate.artist_id) || tr("Artista")}</b><span className="rounded-full border px-2.5 py-1 text-xs">{candidacyLabel(candidate.status)}</span></div>)}
                  {!(detailProject.candidate_summary || []).length && <p className="text-sm text-muted-foreground">{tr("Nessun artista candidato.")}</p>}
                </div>
              </section>

              <section className="rounded-xl border p-4">
                <div className="mb-3"><p className="label-tiny text-primary">{tr("Proposte ricevute")}</p><h3 className="font-semibold">{tr("Confronta le risposte degli artisti")}</h3></div>
                {loadingDetail ? <p className="text-sm text-muted-foreground">{tr("Caricamento…")}</p> : <div className="space-y-3">
                  {detailProposals.map((proposal)=>{
                    const snapshot = proposal.available_project_snapshot || {};
                    const priceFrom = proposal.price_from ?? snapshot.price_from;
                    const priceTo = proposal.price_to ?? snapshot.price_to;
                    return <div key={proposal.id} className="rounded-xl border bg-background/40 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2"><div><b>{artistName(proposal.artist_id) || tr("Artista")}</b><p className="text-xs text-muted-foreground">{proposal.proposal_type==="available_project"?tr("Progetto personale proposto"):tr("Proposta sviluppata sul brief")}</p></div><span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold">{tr("RICEVUTA")}</span></div>
                      <h4 className="mt-3 font-semibold">{proposal.title || snapshot.title || detailProject.title}</h4>
                      {(proposal.preview_path || proposal.available_project_id) && <AuthenticatedApiImage path={`/studio/project-proposals/${proposal.id}/image`} alt={proposal.title||tr("Proposta")} className="mt-3 max-h-[440px] w-full rounded-xl border object-contain bg-black/20" fallback={<div className="mt-3 rounded-xl border p-6 text-center text-xs text-muted-foreground">{tr("Caricamento anteprima…")}</div>}/>}
                      {(proposal.description || snapshot.description) && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{proposal.description || snapshot.description}</p>}
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {(priceFrom!=null || priceTo!=null) && <span className="rounded-full border px-2.5 py-1">{priceFrom!=null?fmt.c(priceFrom):"—"}{priceTo!=null?` – ${fmt.c(priceTo)}`:""}</span>}
                        {proposal.duration_hint_minutes && <span className="rounded-full border px-2.5 py-1">{tr("circa")} {Number(proposal.duration_hint_minutes)/60} {tr("h")}</span>}
                        {proposal.requires_consultation && <span className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-primary">{tr("Previa consulenza")}</span>}
                        <span className="rounded-full border px-2.5 py-1">{proposal.status === "submitted" ? tr("Da presentare") : proposal.status === "presented" ? tr("Visibile al Cliente") : proposal.status === "accepted" ? tr("Accettata dal Cliente") : proposal.status === "rejected" ? tr("Rifiutata dal Cliente") : proposal.status}</span>
                      </div>
                      {proposal.status === "submitted" && <Button type="button" size="sm" className="mt-3" onClick={()=>presentProposal(proposal)}>{tr("Prepara preventivo e presenta")}</Button>}
                    </div>;
                  })}
                  {!detailProposals.length && <p className="text-sm text-muted-foreground">{tr("Nessuna proposta inviata per ora.")}</p>}
                </div>}
              </section>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
